<template>
  <main id="charge">
    <Loading v-if="isLoading" />
    <template v-else>
      <section class="section-top">
        <ConnectorSpec :connector="connector" />
      </section>
      <FeeInfoCard :unitPrice="unitPrice" />
      <section class="section-main">
        <div class="section-main__title">
          <h1>享受超高速充電</h1>
          <h1>方便、可靠而且經濟實惠</h1>
        </div>
        <div class="section-main__action">
          <button
            class="button is-fullwidth is-fill is-rounded"
            type="button"
            :disabled="isProcessing"
            @click="handleClick()"
          >
            下一步
          </button>
        </div>
      </section>
      <div class="modal" :class="{ active: isModalOpen }">
        <PriceAnnouncementModal
          @price-adjust="handleCancel"
          :current-price="currentPrice"
        />
      </div>
    </template>
    <section id="mask" v-if="!isCloseConfirmCard">
      <div class="confirmCard">
        <img src="../assets/photo/confirmCardBg.png" alt="" />
        <div class="confirmCard-Body">
          <div class="logo">
            <inline-svg
              width="60"
              height="60"
              :src="require('../assets/icon/svg/logo.svg')"
            />
          </div>
          <div class="titleBox">
            <div class="title">下載 U-POWER App</div>
            <div class="subtitle">獲得最佳充電體驗及會員優惠</div>
          </div>
        </div>
        <div class="confirmCard-Footer">
          <button @click="isCloseConfirmCard = true">下次再說</button>
          <button @click="redirectToAppPage">立即前往下載</button>
        </div>
      </div>
      <!--
        「在 App 中開啟」：給已安裝 U-POWER App 的使用者，用 upower:// 帶著充電槍資料開啟 App。
        - 只有網址帶 chargebox + connectorno 時才顯示（見 computed.appSchemeLink）
        - 必須由使用者主動點擊才觸發：若自動觸發，未安裝 App 的 iOS 使用者會看到
          Safari「無法打開網頁」的錯誤提示，而本頁的主要使用者正是未安裝 App 的訪客
      -->
      <button v-if="appSchemeLink" class="open-app" @click="openApp">
        已安裝 App？在 App 中開啟
      </button>
    </section>
  </main>
</template>
<script>
import FeeInfoCard from "../components/Connector/FeeInfoCard.vue";
import Loading from "../components/Loading.vue";
import ConnectorSpec from "../components/Connector/ConnectorSpec.vue";
import PriceAnnouncementModal from "../components/Connector/PriceAnnouncementModal.vue";
import chargingAPI from "../apis/charging";
import connectorAPI from "../apis/connector";

export default {
  name: "ConnectorApp",
  data() {
    return {
      isCloseConfirmCard: false,
      isProcessing: false,
      userAgent: "",
      connectorStatus: {
        Available: 1,
        Unavailable: 2,
        Preparing: 3,
        Charging: 4,
        SuspendedEVSE: 5,
        SuspendedEV: 6,
        Finishing: 7,
        Faulted: 8,
        Remove: 9,
        Reserved: 10,
        Offline: 11,
        UnKnow: 12,
      },
      unitPrice: {
        pricingPlanId: "",
        unitPrice: 0,
      },
      currentPrice: {
        pricingPlanId: "",
        unitPrice: 0,
      },
      currentConnectorStatus: {
        connectorId: "",
        status: 0,
      },
      isLoading: false,
      isModalOpen: false,
      connector: {
        connectorId: "",
        stationId: "",
        boxId: "",
        connector: "",
        current: 0,
        position: 1,
        connectorType: {
          connectorTypeId: 0,
          connectorType: "",
        },
        flag: 1,
        status: 1,
        available: 1,
      },
    };
  },
  components: { FeeInfoCard, Loading, ConnectorSpec, PriceAnnouncementModal },
  computed: {
    /**
     * 開啟 U-POWER App 並導到指定充電槍的 Custom URL Scheme
     *
     * 格式：upower://Charging?chargebox=<能源櫃編號>&connectorno=<槍號>
     * 此格式與後端 qrcode.u-power.app 轉址頁所用的相同，App 端已支援解析，
     * 若 App 端調整 scheme 格式，這裡需同步修改。
     *
     * 只有從槍櫃 QR Code 進入（route query 帶 chargebox + connectorno）才組得出連結；
     * 從手動輸入充電槍代碼進入時，route query 只有 connectorId + stationId，
     * 無法對應到 App 的 scheme 參數，此時回傳空字串，按鈕不顯示。
     *
     * @returns {string} scheme 連結；資料不足時回傳 ""
     */
    appSchemeLink() {
      const { chargebox, connectorno } = this.$route.query;
      if (!chargebox || !connectorno) {
        return "";
      }
      return `upower://Charging?chargebox=${chargebox}&connectorno=${connectorno}`;
    },
  },
  async mounted() {
    this.scrollToTop();
    const { chargebox, connectorno, connectorId, stationId } =
      this.$route.query;
    if (
      connectorId &&
      stationId &&
      connectorId.length === 36 &&
      stationId.length === 36
    ) {
      this.checkConnectorState(connectorId);
    } else if (
      chargebox &&
      connectorno &&
      chargebox.length === 25 &&
      connectorno.length === 1
    ) {
      this.fetchConnectorByBoxCodeConnectorNo({
        chargebox: chargebox,
        connectorno: connectorno,
      });
    } else {
      this.$router.push("/search");
    }

    this.checkDeviceOs();
  },
  methods: {
    /**
     * 透過 upower:// 開啟 App
     * - 已安裝：iOS 會跳出「要在 U-POWER 中打開嗎？」確認框，Android 直接開啟 App
     * - 未安裝：瀏覽器無法處理此 scheme，會顯示錯誤或無反應，使用者停留在本頁
     */
    openApp() {
      window.location = this.appSchemeLink;
    },
    redirectToAppPage() {
      const googlePlayUrl =
        "https://play.google.com/store/apps/details?id=com.u_power";
      const appStoreUrl = "https://apps.apple.com/tw/app/u-power/id1612379206";

      if (this.userAgent === "Android") {
        window.location.href = googlePlayUrl;
        return;
      }
      if (this.userAgent === "IOS") {
        window.location = appStoreUrl;
        return;
      }
      if (this.userAgent === "WindowsDesktop") {
        window.open(
          googlePlayUrl,
          "_blank",
          "location=yes,height=570,width=520,scrollbars=yes,status=yes"
        );
        return;
      }
      if (this.userAgent === "MacDesktop") {
        window.open(
          appStoreUrl,
          "_blank",
          "location=yes,height=570,width=520,scrollbars=yes,status=yes"
        );
        return;
      }
    },
    /**
     * 判斷裝置平台並寫入 this.userAgent，供「立即前往下載」按鈕（redirectToAppPage）
     * 決定要前往 App Store 或 Google Play。
     *
     * 注意：這裡只負責判斷平台，「不會」自動轉址到商店。
     * 過去曾以 watch: userAgent 在判斷完成後自動呼叫 redirectToAppPage()，
     * 但從 qrcode.u-power.app 轉址進來時，會蓋掉 iOS「要在 U-POWER 中打開嗎？」的確認框，
     * 也讓未安裝 App 的訪客無法停留在網頁充電，因此已移除。請勿再加回自動轉址。
     */
    checkDeviceOs() {
      let userAgent = navigator.userAgent;

      if (userAgent.match(/Android/i)) {
        if (userAgent.match(/Chrome/i)) {
          // Android + Chrome
          setTimeout(() => {
            if (!document.webkitHidden) {
              this.userAgent = "Android";
              return;
            }
          }, 1000);
        } else {
          // Android + 非 Chrome
          this.userAgent = "Android";
          return;
        }
      } else if (userAgent.match(/(iPhone|iPad|iPod)/i)) {
        setTimeout(() => {
          this.userAgent = "IOS";
          return;
        }, 25);
      } else if (userAgent.match(/(Macintosh|Mac)/i)) {
        // Mac Desktop Device
        setTimeout(() => {
          this.userAgent = "MacDesktop";
          return;
        }, 25);
      } else if (userAgent.match(/(Windows)/i)) {
        // Windows Desktop Device
        setTimeout(() => {
          this.userAgent = "WindowsDesktop";
          return;
        }, 25);
      } else {
        //其他
        let loadDateTime = new Date();
        setTimeout(() => {
          let timeOutDateTime;
          timeOutDateTime = new Date();
          if (timeOutDateTime - loadDateTime < 5000) {
            this.userAgent = "Android";
            return;
          }
        }, 1000);
      }
    },
    async checkConnectorState(connectorid) {
      try {
        this.isLoading = true;
        const { connectorId, stationId } = this.$route.query;
        const { data, status } = await connectorAPI.connector.getStatus({
          connectorid,
        });
        if (!(status >= 200 && status < 300)) {
          throw new Error(status);
        }
        this.currentConnectorStatus = data;
        if (
          this.currentConnectorStatus.status ===
            this.connectorStatus.Unavailable ||
          this.currentConnectorStatus.status ===
            this.connectorStatus.Charging ||
          this.currentConnectorStatus.status ===
            this.connectorStatus.SuspendedEVSE ||
          this.currentConnectorStatus.status ===
            this.connectorStatus.SuspendedEV ||
          this.currentConnectorStatus.status ===
            this.connectorStatus.Finishing ||
          this.currentConnectorStatus.status === this.connectorStatus.Faulted ||
          this.currentConnectorStatus.status === this.connectorStatus.Remove ||
          this.currentConnectorStatus.status ===
            this.connectorStatus.Reserved ||
          this.currentConnectorStatus.status === this.connectorStatus.Offline ||
          this.currentConnectorStatus.status === this.connectorStatus.UnKnow
        ) {
          this.$router.push({
            name: "BeingUsedHint",
            params: {
              connectorId: connectorId
                ? connectorId
                : this.connector.connectorId,
              stationId: stationId ? stationId : this.connector.stationId,
            },
          });
        } else {
          const { connectorId, stationId } = this.$route.query;
          if (connectorId) {
            this.fetchConnector(connectorId);
          }
          this.$store.dispatch("fetchStation", {
            stationid: stationId ? stationId : this.connector.stationId,
          });
          const payload = stationId ? stationId : this.connector.stationId;
          this.getUnitPrice(payload);
          this.isLoading = false;
        }
      } catch (error) {
        this.isLoading = false;
        console.error(error.message);
      }
    },
    async fetchConnectorByBoxCodeConnectorNo(chargebox, connectorno) {
      try {
        this.isLoading = true;
        const { data, status } =
          await connectorAPI.connector.getByBoxCodeConnectorNo(
            chargebox,
            connectorno
          );
        if (!(status >= 200 && status < 300)) {
          throw new Error(status);
        }
        if (status === 204) {
          this.$router.push({
            path: "/404",
          });
        }
        this.connector = data;
        this.checkConnectorState(this.connector.connectorId);
      } catch (error) {
        this.isLoading = false;
        console.error(error.message);
      }
    },
    async getUnitPrice(stationid) {
      try {
        const { data, status } = await chargingAPI.charging.getUnitPrice({
          stationid,
        });
        if (!(status >= 200 && status < 300)) {
          throw new Error(status);
        }
        this.unitPrice = data;
      } catch (error) {
        console.error(error.message);
        this.isLoading = false;
      }
    },
    handleCancel() {
      this.unitPrice = this.currentPrice;
      this.handleModal();
    },
    scrollToTop() {
      window.scroll({
        top: 0,
        behavior: "smooth",
      });
    },
    async fetchConnector(connectorid) {
      try {
        const { data, status } = await connectorAPI.connector.getByConnectorId({
          connectorid: connectorid,
        });
        if (!(status >= 200 && status < 300)) {
          throw new Error(status);
        }
        this.connector = data;
      } catch (error) {
        console.error(error.message);
      }
    },
    handleModal() {
      this.isModalOpen = !this.isModalOpen;
    },
    async handleClick() {
      try {
        const { data, status } = await chargingAPI.charging.getUnitPrice({
          stationid: this.connector.stationId,
        });
        if (!(status >= 200 && status < 300)) {
          throw new Error(status);
        }
        this.currentPrice = data;
        if (this.currentPrice.unitPrice !== this.unitPrice.unitPrice) {
          this.handleModal();
        } else {
          this.$router.push({
            name: "PlugHint",
            params: {
              connectorId: this.connector.connectorId,
              stationId: this.connector.stationId,
            },
          });
        }
      } catch (error) {
        console.error(error.message);
      }
    },
    async handleClickBack() {
      try {
        this.$router.push({
          name: "ConnectorSearch",
        });
      } catch (error) {
        console.error(error.message);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#mask {
  position: fixed;
  display: flex;
  // 垂直排列：下載提示卡在上、「在 App 中開啟」按鈕在下
  flex-direction: column;
  justify-content: center;
  align-items: center;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  // background: rgba(255, 255, 255, 0.8);
  background: rgba(0, 0, 0, 0.8);
  z-index: 999;
  .confirmCard {
    display: flex;
    flex-direction: column;
    height: auto;
    width: 90vw;
    max-width: 350px;
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    img {
      position: absolute;
      left: 0;
      top: 0;
      width: auto;
      height: 75%;
      z-index: -10;
      border-top-left-radius: 12px;
      border-top-right-radius: 12px;
    }
    .confirmCard-Body {
      padding: 2rem 1.5rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 100%;
      width: 100%;
      .logo {
        width: 60px;
        height: 60px;
        margin-right: 1.2rem;
        border-radius: 12px;
        overflow: hidden;
      }
      .titleBox {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-start;
        flex-grow: 1;
        color: $white-bis;
        .title {
          margin-bottom: 0.75rem;
          font-size: $size-5;
          font-weight: $weight-bold;
        }
        .subtitle {
          font-size: $size-7;
        }
        @media screen and (min-width: 380px) {
          .title {
            font-size: 23px;
          }
          .subtitle {
            font-size: 16px;
          }
        }
        @media screen and (max-width: 350px) {
          .title,
          .subtitle {
            font-size: 12px;
          }
        }
      }
    }
    .confirmCard-Footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      button {
        padding: 1.25rem 0;
        display: block;
        width: 50%;
        background: none;
        border: none;
        box-shadow: inset 0 0 0 1px $primary;
        font-size: $size-6;
        &:nth-child(1) {
          border-bottom-left-radius: 12px;
          background: $white;
          color: $primary;
        }
        &:nth-child(2) {
          border-bottom-right-radius: 12px;
          background: $primary;
          color: $white-bis;
        }
      }
      @media screen and (max-width: 350px) {
        button {
          font-size: 12px;
        }
      }
    }
  }
  // 「在 App 中開啟」：位於深色遮罩上，用白色底線文字呈現為次要操作，不與下載按鈕搶視覺焦點
  .open-app {
    margin-top: 1.25rem;
    padding: 0.5rem;
    background: none;
    border: none;
    color: $white-bis;
    font-size: $size-6;
    text-decoration: underline;
  }
}
</style>
